/* globals csrfval generateMasterplanUrl saveOverrideUrl masterplanDataUrl revertOverrideUrl */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import MasterplanTableRow from './MasterplanTableRow';

import CheckboxInput from 'CommonComponents/CheckboxInput';
import TableFilterModal from 'CommonComponents/TableFilterModal';
import Tooltip from 'CommonComponents/Tooltip';

/**
 * Numeric columns that can be overridden — must match OVERRIDABLE_COLUMNS on the backend.
 */
const OVERRIDABLE_COLS = [
    'athletes',
    'team_support',
    'total_team',
    'td',
    'ito',
    'nto',
    'total_officials',
    'preliminary_session',
    'medal_sessions',
    'total_sessions',
    'session_days',
    'event_days',
];

// Positional indices of overridable columns inside the row array
const COL_INDEX = {
    athletes: 3,
    team_support: 4,
    total_team: 5,
    td: 6,
    ito: 7,
    nto: 8,
    total_officials: 9,
    preliminary_session: 10,
    medal_sessions: 11,
    total_sessions: 12,
    session_days: 13,
    event_days: 14,
};

function MasterplanTable() {
    const [includes, setIncludes] = useState({
        athletes: 1,
        team_support: 1,
        total_team: 1,
        td: 1,
        ito: 1,
        nto: 1,
        total_officials: 1,
        preliminary_session: 1,
        medal_sessions: 1,
        total_sessions: 1,
        session_days: 1,
        event_days: 1,
        venue_type: 1,
        venue_capacity: 1,
        hire_start: 1,
        hire_end: 1,
        revenues: 1,
        expenses: 1,
        net: 1,
    });
    const [csrf] = useState(csrfval);
    const [generateUrl] = useState(generateMasterplanUrl);
    const [isLoading, setIsLoading] = useState(true);
    const [tableData, setTableData] = useState([]);
    const [tableRowSpans, setTableRowSpans] = useState([]);
    const [venueUrls, setVenueUrls] = useState([]);
    const [sportUrls, setSportUrls] = useState([]);
    const [firstCompetitionVenueIndex, setFirstCompetitionVenueIndex] = useState(-1);
    const [firstNonCompetitionVenueIndex, setFirstNonCompetitionVenueIndex] = useState(-1);
    const [isSplit, setIsSplit] = useState(false);
    const [overrides, setOverrides] = useState({});
    const [totals, setTotals] = useState(null);
    const [venueSummary, setVenueSummary] = useState(null);
    const [rowMeta, setRowMeta] = useState([]);

    useEffect(() => {
        getData();
    }, []);

    const changeInclude = (e, key) => {
        const inc = { ...includes };
        inc[key] = inc[key] ? 0 : 1;
        setIncludes(inc);
    };

    const getData = () => {
        setIsLoading(true);
        axios
            .post(masterplanDataUrl, { include: includes })
            .then((response) => {
                const {
                    data,
                    rowSpans,
                    venueUrls,
                    sportUrls,
                    totals: apiTotals,
                    overrides: apiOverrides,
                    rowMeta: apiRowMeta,
                    summary: apiSummary,
                } = response.data;
                setTableData(data);
                setTableRowSpans(rowSpans);
                setVenueUrls(venueUrls);
                setSportUrls(sportUrls);
                setFirstCompetitionVenueIndex(data.findIndex((row) => row[2] == 'Competition'));
                setFirstNonCompetitionVenueIndex(data.findIndex((row) => row[2] == 'Non Competition'));
                setTotals(apiTotals || null);
                setVenueSummary(apiSummary || null);
                setOverrides(apiOverrides || {});
                setRowMeta(apiRowMeta || []);
                setIsLoading(false);
            })
            .catch((e) => {
                console.log(e.response);
            });
    };

    const DRIVER_COLUMNS = ['athletes', 'event_days'];

    const doSaveOverride = (venueId, sportId, column, value) => {
        axios
            .post(saveOverrideUrl, {
                venue_id: venueId,
                sport_id: sportId || null,
                column_name: column,
                value: value,
            })
            .then(() => {
                getData();
            })
            .catch((e) => {
                console.error('Failed to save override', e.response);
            });
    };

    const handleSaveOverride = (venueId, sportId, column, value) => {
        if (!venueId) return;

        if (!window.quantityLinkEnabled || !window.quantityLinkMasterplanCellImpactPreviewUrl || !DRIVER_COLUMNS.includes(column) || !sportId) {
            doSaveOverride(venueId, sportId, column, value);
            return;
        }

        const previewParams = new URLSearchParams({
            venue_id: String(venueId),
            sport_id: String(sportId),
            column: column,
            new_value: String(value),
        });
        const previewUrl = window.quantityLinkMasterplanCellImpactPreviewUrl + '?' + previewParams.toString();

        const dependentsParams = new URLSearchParams({
            source_type: column === 'athletes' ? 'App\\Sport' : 'App\\Event',
            source_id: String(column === 'athletes' ? sportId : window.eventSourceId),
            ...(column === 'event_days' ? { driver: 'event_days' } : {}),
        });
        const dependentsUrl = window.quantityLinkDriverDependentsUrl + '?' + dependentsParams.toString();

        window.dispatchEvent(
            new CustomEvent('quantityimpact:check', {
                detail: {
                    previewUrl: previewUrl,
                    newValue: value,
                    formEl: null,
                    onConfirm: () => doSaveOverride(venueId, sportId, column, value),
                    dependentsUrl: dependentsUrl,
                    sourceLabel: null,
                },
            }),
        );
    };

    const handleRevertOverride = (venueId, sportId, column) => {
        if (!venueId) return;

        axios
            .delete(revertOverrideUrl, {
                data: {
                    venue_id: venueId,
                    sport_id: sportId || null,
                    column_name: column,
                },
            })
            .then(() => {
                getData();
            })
            .catch((e) => {
                console.error('Failed to revert override', e.response);
            });
    };

    /**
     * Build a totals row array in the same positional format as data rows.
     * Non-numeric columns get label/empty strings.
     */
    const buildTotalsRow = (label, totalsData) => {
        if (!totalsData) return null;

        const row = new Array(24).fill('');
        row[0] = label;
        row[1] = '';
        row[2] = '';

        OVERRIDABLE_COLS.forEach((col) => {
            const idx = COL_INDEX[col];
            row[idx] = totalsData.totals[col] !== undefined ? totalsData.totals[col] : 0;
        });

        return row;
    };

    // SVG Icons
    const ChevronRightIcon = (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18l6-6-6-6"></path>
        </svg>
    );

    const ChevronDownIcon = (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9l6 6 6-6"></path>
        </svg>
    );

    // Find the last index of competition and non-competition rows so we know where to insert subtotals
    const lastCompetitionIndex = (() => {
        let last = -1;
        tableData.forEach((row, idx) => {
            if (row[2] === 'Competition') last = idx;
        });
        return last;
    })();

    const lastNonCompetitionIndex = tableData.length - 1;

    const handleGenerateSubmit = (e) => {
        if (!window.quantityLinkEnabled || !window.quantityLinkDriverImpactPreviewUrl) return;

        const params = new URLSearchParams({
            source_type: 'App\\Event',
            source_id: String(window.eventSourceId),
            driver: 'event_days',
        });
        const previewUrl = window.quantityLinkDriverImpactPreviewUrl + '?' + params.toString();

        e.preventDefault();
        window.dispatchEvent(
            new CustomEvent('quantityimpact:check', {
                detail: {
                    previewUrl: previewUrl,
                    newValue: (window.currentEventDays || 0) + 1,
                    formEl: e.target,
                    onConfirm: null,
                },
            }),
        );
    };

    return (
        <div>
            <form method="POST" action={generateUrl} onSubmit={handleGenerateSubmit}>
                <input type="hidden" name="_token" value={csrf} />
                <div className="table__card">
                    <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
                        {venueSummary && venueSummary.venueCount > 0 && (
                            <Tooltip
                                placement="bottom-start"
                                tooltipType="content"
                                trigger={['hover', 'focus']}
                                tooltipContent={
                                    <div className="tw-text-white tw-text-sm tw-space-y-1">
                                        <div className="tw-flex tw-justify-between tw-gap-6">
                                            <span>{$t('Competition Venues')}</span>
                                            <span className="num tw-font-semibold">{venueSummary.competitionVenueCount || 0}</span>
                                        </div>
                                        <div className="tw-flex tw-justify-between tw-gap-6">
                                            <span>{$t('Non Competition Venues')}</span>
                                            <span className="num tw-font-semibold">{venueSummary.nonCompetitionVenueCount || 0}</span>
                                        </div>
                                    </div>
                                }
                            >
                                <button
                                    type="button"
                                    className="venue-count-chip tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-rounded-full tw-bg-blue-10 tw-border tw-border-blue-25 tw-text-sm tw-font-medium tw-text-blue tw-cursor-default"
                                    data-source="venue-count"
                                    aria-label={`${$t('Venues')}: ${venueSummary.venueCount} (${venueSummary.competitionVenueCount || 0} ${$t('competition')}, ${venueSummary.nonCompetitionVenueCount || 0} ${$t('non-competition')})`}
                                >
                                    {$t('Venues')} <span className="num tw-font-semibold">{venueSummary.venueCount}</span>
                                </button>
                            </Tooltip>
                        )}
                        <div className="tw-ml-auto">
                            <TableFilterModal id="expenditures-table_filter-modal">
                                <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                                    <CheckboxInput
                                        name="include[athletes]"
                                        data-key="athletes"
                                        id="include[athletes]"
                                        defaultChecked={!!includes.athletes}
                                        value={includes.athletes}
                                        label={$t('Athletes')}
                                        onChange={(e) => changeInclude(e, 'athletes')}
                                    />
                                    <CheckboxInput
                                        name="include[team_support]"
                                        data-key="team_support"
                                        id="include[team_support]"
                                        defaultChecked={!!includes.team_support}
                                        value={includes.team_support}
                                        label={$t('Team Support')}
                                        onChange={(e) => changeInclude(e, 'team_support')}
                                    />
                                    <CheckboxInput
                                        name="include[total_team]"
                                        data-key="total_team"
                                        id="include[total_team]"
                                        defaultChecked={!!includes.total_team}
                                        value={includes.total_team}
                                        label={$t('Total Team')}
                                        onChange={(e) => changeInclude(e, 'total_team')}
                                    />
                                    <CheckboxInput
                                        name="include[td]"
                                        data-key="td"
                                        id="include[td]"
                                        defaultChecked={!!includes.td}
                                        value={includes.td}
                                        label={$t('TDs')}
                                        onChange={(e) => changeInclude(e, 'td')}
                                    />
                                    <CheckboxInput
                                        name="include[ito]"
                                        data-key="ito"
                                        id="include[ito]"
                                        defaultChecked={!!includes.ito}
                                        value={includes.ito}
                                        label={$t('ITO')}
                                        onChange={(e) => changeInclude(e, 'ito')}
                                    />
                                    <CheckboxInput
                                        name="include[nto]"
                                        data-key="nto"
                                        id="include[nto]"
                                        defaultChecked={!!includes.nto}
                                        value={includes.nto}
                                        label={$t('NTO')}
                                        onChange={(e) => changeInclude(e, 'nto')}
                                    />
                                    <CheckboxInput
                                        name="include[total_officials]"
                                        data-key="total_officials"
                                        id="include[total_officials]"
                                        defaultChecked={!!includes.total_officials}
                                        value={includes.total_officials}
                                        label={$t('Total Officials')}
                                        onChange={(e) => changeInclude(e, 'total_officials')}
                                    />
                                    <CheckboxInput
                                        name="include[preliminary_session]"
                                        data-key="preliminary_session"
                                        id="include[preliminary_session]"
                                        defaultChecked={!!includes.preliminary_session}
                                        value={includes.preliminary_session}
                                        label={$t('Preliminary Sessions')}
                                        onChange={(e) => changeInclude(e, 'preliminary_session')}
                                    />
                                    <CheckboxInput
                                        name="include[medal_sessions]"
                                        data-key="medal_sessions"
                                        id="include[medal_sessions]"
                                        defaultChecked={!!includes.medal_sessions}
                                        value={includes.medal_sessions}
                                        label={$t('Medal Sessions')}
                                        onChange={(e) => changeInclude(e, 'medal_sessions')}
                                    />
                                    <CheckboxInput
                                        name="include[total_sessions]"
                                        data-key="total_sessions"
                                        id="include[total_sessions]"
                                        defaultChecked={!!includes.total_sessions}
                                        value={includes.total_sessions}
                                        label={$t('Total Sessions')}
                                        onChange={(e) => changeInclude(e, 'total_sessions')}
                                    />
                                    <CheckboxInput
                                        name="include[session_days]"
                                        data-key="session_days"
                                        id="include[session_days]"
                                        defaultChecked={!!includes.session_days}
                                        value={includes.session_days}
                                        label={$t('Event Period')}
                                        onChange={(e) => changeInclude(e, 'session_days')}
                                    />
                                    <CheckboxInput
                                        name="include[event_days]"
                                        data-key="event_days"
                                        id="include[event_days]"
                                        defaultChecked={!!includes.event_days}
                                        value={includes.event_days}
                                        label={$t('Event Days')}
                                        onChange={(e) => changeInclude(e, 'event_days')}
                                    />
                                    <CheckboxInput
                                        name="include[venue_type]"
                                        data-key="venue_type"
                                        id="include[venue_type]"
                                        defaultChecked={!!includes.venue_type}
                                        value={includes.venue_type}
                                        label={$t('Venue Type')}
                                        onChange={(e) => changeInclude(e, 'venue_type')}
                                    />
                                    <CheckboxInput
                                        name="include[venue_capacity]"
                                        data-key="venue_capacity"
                                        id="include[venue_capacity]"
                                        defaultChecked={!!includes.venue_capacity}
                                        value={includes.venue_capacity}
                                        label={$t('Venue Capacity')}
                                        onChange={(e) => changeInclude(e, 'venue_capacity')}
                                    />
                                    <CheckboxInput
                                        name="include[hire_start]"
                                        data-key="hire_start"
                                        id="include[hire_start]"
                                        defaultChecked={!!includes.hire_start}
                                        value={includes.hire_start}
                                        label={$t('Hire period start')}
                                        onChange={(e) => changeInclude(e, 'hire_start')}
                                    />
                                    <CheckboxInput
                                        name="include[hire_end]"
                                        data-key="hire_end"
                                        id="include[hire_end]"
                                        defaultChecked={!!includes.hire_end}
                                        value={includes.hire_end}
                                        label={$t('Hire period end')}
                                        onChange={(e) => changeInclude(e, 'hire_end')}
                                    />
                                    <CheckboxInput
                                        name="include[revenues]"
                                        data-key="revenues"
                                        id="include[revenues]"
                                        defaultChecked={!!includes.revenues}
                                        value={includes.revenues}
                                        label={$t('Revenue')}
                                        onChange={(e) => changeInclude(e, 'revenues')}
                                    />
                                    <CheckboxInput
                                        name="include[expenses]"
                                        data-key="expenses"
                                        id="include[expenses]"
                                        defaultChecked={!!includes.expenses}
                                        value={includes.expenses}
                                        label={$t('Expenditure')}
                                        onChange={(e) => changeInclude(e, 'expenses')}
                                    />
                                    <CheckboxInput
                                        name="include[net]"
                                        data-key="net"
                                        id="include[net]"
                                        defaultChecked={!!includes.net}
                                        value={includes.net}
                                        label={$t('Net')}
                                        onChange={(e) => changeInclude(e, 'net')}
                                    />
                                </div>
                            </TableFilterModal>
                        </div>
                    </div>
                    <div className="inbud-table_wrapper">
                        <table className="inbud-table tw-table-fixed">
                            <thead>
                                <tr className="">
                                    <th className="tw-w-56 tw-sticky tw-left-0 tw-z-10 !tw-bg-blue-25 tw-h-fit">{$t('Venue Name')}</th>
                                    <th className="tw-w-56 tw-sticky tw-left-56 tw-z-10 !tw-bg-blue-25 tw-h-fit">{$t('Event')}</th>
                                    <th className="tw-w-32 tw-sticky tw-left-[28rem] tw-z-10 !tw-bg-blue-25 tw-h-fit">{$t('Competition')}</th>
                                    {!!includes['athletes'] && <th className="tw-w-32">{$t('Athletes')}</th>}
                                    {!!includes['team_support'] && <th className="tw-w-32">{$t('Team Support')}</th>}
                                    {!!includes['total_team'] && <th className="tw-w-32">{$t('Total Team')}</th>}
                                    {!!includes['td'] && <th className="tw-w-32">{$t('TDs')}</th>}
                                    {!!includes['ito'] && <th className="tw-w-32">{$t('ITOs')}</th>}
                                    {!!includes['nto'] && <th className="tw-w-32">{$t('NTOs')}</th>}
                                    {!!includes['total_officials'] && <th className="tw-w-32">{$t('Total Officials')}</th>}
                                    {!!includes['preliminary_session'] && <th className="tw-w-32">{$t('Preliminary Sessions')}</th>}
                                    {!!includes['medal_sessions'] && <th className="tw-w-32">{$t('Medal Sessions')}</th>}
                                    {!!includes['total_sessions'] && <th className="tw-w-32">{$t('Total Sessions')}</th>}
                                    {!!includes['session_days'] && <th className="tw-w-32">{$t('Event Period')}</th>}
                                    {!!includes['event_days'] && <th className="tw-w-32">{$t('Event Days')}</th>}
                                    {!!includes['venue_type'] && <th className="tw-w-32">{$t('Venue Type')}</th>}
                                    {!!includes['venue_capacity'] && <th className="tw-w-32">{$t('Venue Capacity')}</th>}
                                    {!!includes['hire_start'] && <th className="tw-w-32">{$t('Hire period start')}</th>}
                                    {!!includes['hire_end'] && <th className="tw-w-32">{$t('Hire period end')}</th>}
                                    {!!includes['revenues'] && <th className="tw-w-32">{$t('Revenue')}</th>}
                                    {!!includes['expenses'] &&
                                        (isSplit ? (
                                            <th className="tw-w-[350px] tw-text-center">
                                                <div
                                                    onClick={() => setIsSplit(!isSplit)}
                                                    className="tw-cursor-pointer tw-flex tw-justify-center tw-items-center tw-gap-2"
                                                >
                                                    {$t('Expenditure')}
                                                    {isSplit ? ChevronDownIcon : ChevronRightIcon}
                                                </div>
                                                {/* Breakdown */}
                                                <div className="tw-flex tw-justify-between tw-mt-2">
                                                    <span className="tw-w-1/3 tw-text-center tw-font-semibold">{$t('Workforce Expenditure')}</span>
                                                    <span className="tw-w-1/3 tw-text-center tw-font-semibold">{$t('Non Workforce Expenditure')}</span>
                                                    <span className="tw-w-1/3 tw-text-center tw-font-semibold">{$t('Total Expenditure')}</span>
                                                </div>
                                            </th>
                                        ) : (
                                            <th className="tw-w-32">
                                                <div
                                                    onClick={() => setIsSplit(!isSplit)}
                                                    className="tw-cursor-pointer tw-flex tw-justify-center tw-items-center tw-gap-2"
                                                >
                                                    {$t('Expenditure')}
                                                    {isSplit ? ChevronDownIcon : ChevronRightIcon}
                                                </div>
                                            </th>
                                        ))}
                                    {!!includes['net'] && <th className="tw-w-32">{$t('Net')}</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {!!isLoading && (
                                    <tr>
                                        <td colSpan={23}>
                                            <span className="text-dark-blue">
                                                <b>Loading...</b>
                                            </span>
                                        </td>
                                    </tr>
                                )}
                                {tableData.map((row, index) => {
                                    const isLastCompetition = index === lastCompetitionIndex && lastCompetitionIndex >= 0;
                                    const isLastRow = index === tableData.length - 1;

                                    const meta = rowMeta[index] || {};
                                    const venueId = meta.venueId || null;
                                    const sportId = meta.sportId || null;

                                    return (
                                        <React.Fragment key={index}>
                                            {firstCompetitionVenueIndex == index && (
                                                <tr>
                                                    <td className="tw-font-semibold tw-sticky tw-left-0 tw-z-10 tw-h-fit !tw-border-r-0">
                                                        {$t('Competition Venues')}
                                                    </td>
                                                    <td colSpan={21}></td>
                                                </tr>
                                            )}
                                            {firstNonCompetitionVenueIndex == index && (
                                                <tr>
                                                    <td className="tw-font-semibold tw-sticky tw-left-0 tw-z-10 tw-h-fit !tw-border-r-0">
                                                        {$t('Non Competition Venues')}
                                                    </td>
                                                    <td colSpan={21}></td>
                                                </tr>
                                            )}
                                            <MasterplanTableRow
                                                index={index}
                                                row={row}
                                                includes={includes}
                                                rowSpans={tableRowSpans[index]}
                                                venueurl={venueUrls[index]}
                                                sportUrl={sportUrls[index]}
                                                isSplit={isSplit}
                                                venueId={venueId}
                                                sportId={sportId}
                                                overrides={overrides}
                                                onSave={handleSaveOverride}
                                                onRevert={handleRevertOverride}
                                            />

                                            {/* Competition sub-total row — appended after the last competition row */}
                                            {isLastCompetition && totals && totals.competition && (
                                                <MasterplanTableRow
                                                    index={'subtotal-competition'}
                                                    row={buildTotalsRow($t('Competition Venues subtotal'), totals.competition)}
                                                    includes={includes}
                                                    rowSpans={1}
                                                    venueurl="#"
                                                    sportUrl="#"
                                                    isSplit={isSplit}
                                                    isTotalRow={true}
                                                />
                                            )}

                                            {/* Non-competition sub-total after the very last row */}
                                            {isLastRow && totals && totals.nonCompetition && firstNonCompetitionVenueIndex >= 0 && (
                                                <MasterplanTableRow
                                                    index={'subtotal-noncompetition'}
                                                    row={buildTotalsRow($t('Non Competition Venues subtotal'), totals.nonCompetition)}
                                                    includes={includes}
                                                    rowSpans={1}
                                                    venueurl="#"
                                                    sportUrl="#"
                                                    isSplit={isSplit}
                                                    isTotalRow={true}
                                                />
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                            {/* Grand total row in tfoot for accessibility */}
                            {totals && totals.grand && (
                                <tfoot>
                                    <MasterplanTableRow
                                        index={'grand-total'}
                                        row={buildTotalsRow($t('Total'), totals.grand)}
                                        includes={includes}
                                        rowSpans={1}
                                        venueurl="#"
                                        sportUrl="#"
                                        isSplit={isSplit}
                                        isTotalRow={true}
                                        isGrandTotal={true}
                                    />
                                </tfoot>
                            )}
                        </table>
                    </div>

                    <div className="tw-flex tw-justify-end tw-gap-x-4 tw-mt-4">
                        <button type="submit" className="tw-h-8 tw-flex tw-items-center tw-px-5 tw-bg-blue tw-text-white tw-font-semibold tw-rounded-full">
                            {$t('Generate Report')}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}

if (document.getElementById('venueMasterplan')) {
    var rootEl = document.getElementById('venueMasterplan'); // data attributes below help us
    ReactDOM.render(
        <App>
            <MasterplanTable />
        </App>,
        rootEl,
    );
}
